import { File } from 'app/shared/models/file';
import { IParentEntity } from 'app/shared/interfaces/entity';
import { KeyValuePair } from 'app/shared/models/keyvaluepair';
import { AttachmentService } from 'app/shared/services/attachment.service';
import { DefaultDataService } from 'app/shared/services/default-data.service';
import RemoteFileSystemProvider from 'devextreme/file_management/remote_provider';
import CustomFileSystemProvider from 'devextreme/file_management/custom_provider';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  Component,
  HostListener,
  Inject,
  SecurityContext,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { Router } from '@angular/router';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { DomSanitizer } from '@angular/platform-browser';
import { HyperLawService } from 'app/shared/services/hyper-law.service';
import FileSystemItem from 'devextreme/file_management/file_system_item';
import { MatDialog } from '@angular/material/dialog';
import { CasePrepExportStatus } from 'app/shared/services/hyper-law.service';
import { EventService } from 'app/shared/services/event.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-file-manager',
  templateUrl: './file-manager.component.html',
  styleUrls: ['./file-manager.component.scss'],
})
export class FileManagerComponent implements OnDestroy {
  @ViewChild('fileManager') fileManager: any;
  @HostListener('window:keyup.esc') onKeyUp() {
    this.dialogRef.close();
  }

  fileSystemProvider: CustomFileSystemProvider;
  remoteProvider: RemoteFileSystemProvider;
  currentUploadFileWebkitPath: string = '';
  selection: FileSystemItem[] = [];
  allowedFileExtensions: string[];
  prefix: string = 'subject';
  subjectId: string;
  editLabelsVisible = false;
  labels: string[] = [];
  summary: '';
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  showCasePrepUI = false;
  contextMenuItems: any = [];
  filesUpdated$: Subscription;
  maxFileUploadSize: number = 1024 * 1024 * 10;

  constructor(
    private router: Router,
    private sanitized: DomSanitizer,
    private attachmentService: AttachmentService,
    private defaultDataService: DefaultDataService,
    private eventService: EventService,
    private hyperLawService: HyperLawService,
    public dialogRef: MatDialogRef<FileManagerComponent>,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: IParentEntity
  ) {
    this.refresh = this.refresh.bind(this);
    this.openFiles = this.openFiles.bind(this);
    this.downloadFiles = this.downloadFiles.bind(this);
    this.exportToCasePreparationFile =
      this.exportToCasePreparationFile.bind(this);
    this.openEditLabels = this.openEditLabels.bind(this);
    this.onContextMenuShowing = this.onContextMenuShowing.bind(this);
    this.onContentReady = this.onContentReady.bind(this);
    this.uploadFiles = this.uploadFiles.bind(this);
    this.uploadFolder = this.uploadFolder.bind(this);
    this.onContextMenuItemClick = this.onContextMenuItemClick.bind(this);
    this.onToolbarItemClick = this.onToolbarItemClick.bind(this);

    this.subjectId = <string>this.router.url.split('/').pop();
    this.prefix = this.router.url.split('/')[1];
    if (this.prefix.toLocaleLowerCase() == 'subjects') this.prefix = 'subject';
    // MRD - referrals re-worked so all attachments (for referral or subject) are stored in the subject directory
    if (this.prefix.toLocaleLowerCase() == 'referrals') this.prefix = 'subject';

    this.maxFileUploadSize = this.defaultDataService.maxFileUploadSizeMb * 1024 * 1024;
    this.allowedFileExtensions =
      this.data.type.toLowerCase() === 'tasktype'
        ? ['.docx']
        : this.defaultDataService.allowedFileExtensions
            .split(';')
            .filter((x) => x);

    this.remoteProvider = new RemoteFileSystemProvider({
      endpointUrl: `api/file-manager-azure/${this.prefix}/${this.subjectId}`,
      beforeAjaxSend: ({ formData }) => {
        formData.uploadFileWebkitPath = this.currentUploadFileWebkitPath;
      },
    });

    this.fileSystemProvider = new CustomFileSystemProvider({
      abortFileUpload: (file, uploadInfo, destinationDirectory) =>
        this.remoteProvider.abortFileUpload(
          file,
          uploadInfo,
          destinationDirectory
        ),
      createDirectory: (parentDirectory, name) =>
        this.remoteProvider.createDirectory(parentDirectory, name),
      copyItem: (item, destinationDirectory) =>
        this.remoteProvider.copyItems([item], destinationDirectory),
      deleteItem: (item) => this.remoteProvider.deleteItems([item]),
      downloadItems: () => {},
      getItems: (parent) => this.remoteProvider.getItems(parent),
      getItemsContent: (items) => this.remoteProvider.getItemsContent(items),
      moveItem: (item, destinationDirectory) =>
        this.remoteProvider.moveItems([item], destinationDirectory),
      renameItem: (item, newName) =>
        this.remoteProvider.renameItem(item, newName),
      uploadFileChunk: (file, uploadInfo, destinationDirectory) => {
        if (uploadInfo.chunkIndex === 0) {
          uploadInfo.customData.path = file.webkitRelativePath;
        }
        this.currentUploadFileWebkitPath = uploadInfo.customData.path;

        return this.remoteProvider.uploadFileChunk(
          file,
          uploadInfo,
          destinationDirectory
        );
      },
    });

    this.showCasePrepUI = this.hyperLawService.isConfigValid();

    this.filesUpdated$ = this.eventService.onFilesUpdated.subscribe(() => {
      if (this.fileManager) {
        this.refresh();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.filesUpdated$) {
      this.filesUpdated$.unsubscribe();
    }
  }

  file(file: FileSystemItem) {
    return new File(file, `\\${this.prefix}\\${this.subjectId}`);
  }

  refresh() {
    setTimeout(() => {
      this.fileManager.instance._commandManager.executeCommand('refresh');
    }, 1000);
  }

  uploadFiles() {
    const uploader = this.fileManager.instance._editing._fileUploader;
    const createUploaderBase = uploader._createInternalFileUploader;

    uploader._uploaderInfos.forEach((info: any) => {
      info.fileUploader.option('inputAttr', { webkitdirectory: false });
    });

    uploader._createInternalFileUploader = function () {
      createUploaderBase.call(uploader);
      uploader._uploaderInfos
        .at(-1)
        .fileUploader.option('inputAttr', { webkitdirectory: false });
    };
  }

  uploadFolder() {
    const uploader = this.fileManager.instance._editing._fileUploader;
    const createUploaderBase = uploader._createInternalFileUploader;

    uploader._uploaderInfos.forEach((info: any) => {
      info.fileUploader.option('inputAttr', { webkitdirectory: true });
    });

    uploader._createInternalFileUploader = function () {
      createUploaderBase.call(uploader);
      uploader._uploaderInfos
        .at(-1)
        .fileUploader.option('inputAttr', { webkitdirectory: true });
    };
  }

  getFileItems(): FileSystemItem[] {
    return this.fileManager.instance._editing._model
      .getMultipleSelectedItems()
      ?.map((i: { fileItem: any }) => i?.fileItem);
  }

  openFiles() {
    this.getFileItems().forEach((item) => {
      this.attachmentService
        .getViewOnlineLink(this.file(item))
        .subscribe((url: string) => {
          if (url) {
            setTimeout(() => {
              window.open(
                <string>this.sanitized.sanitize(SecurityContext.URL, url)
              );
            }, 100);
          }
        });
    });
  }

  downloadFiles() {
    this.getFileItems().forEach((item) => {
      this.attachmentService
        .getDownloadLink(this.file(item))
        .subscribe((url: string) => {
          if (url) {
            setTimeout(() => {
              window.open(
                <string>this.sanitized.sanitize(SecurityContext.URL, url)
              );
            }, 100);
          }
        });
    });
  }

  exportToCasePreparationFile() {
    this.hyperLawService.exportToCaseBtnClicked(
      this.subjectId,
      this.getFileItems().map((i) => this.file(i))
    );
  }

  openEditLabels() {
    this.editLabelsVisible = true;
    const dataItem = this.getFileItems()?.shift()?.dataItem?.dataItem;
    this.labels = dataItem.labels ? dataItem.labels.split(',') : [];
    this.summary = dataItem.summary;
  }

  closeEditLabels() {
    this.editLabelsVisible = false;
    this.summary = '';
    this.labels = [];
  }

  addSummary(e: any) {
    this.summary = e.value;
  }

  addLabel(e: any) {
    const input = e.input;
    const value = e.value;
    if ((value || '').trim()) this.labels.push(value.trim());
    if (input) input.value = '';
  }

  removeLabel(label: string) {
    const index = this.labels.indexOf(label);
    if (index >= 0) this.labels.splice(index, 1);
  }

  updateMetadata() {
    const metadata: KeyValuePair[] = [];
    metadata.push(new KeyValuePair('Labels', this.labels.join(', ')));
    metadata.push(new KeyValuePair('Summary', this.summary));
    this.selection.forEach((item) => {
      this.attachmentService.updateMetadata(this.file(item), metadata);
    });
    this.closeEditLabels();
    this.refresh();
  }

  onSelectionChanged(e) {
    this.selection = e.selectedItems || [];
  }

  onItemRenaming(event: any) {
    // console.log('event:', event, '<<<');
    // this.dialog
    //   .open(messageBoxComponent.MessageBoxComponent, {
    //     data: new messageBoxComponent.MessageBoxParameters(
    //       `Renaming this file will remove it from the case preparation file. Do you wish to continue?`,
    //       'Confirm',
    //       messageBoxComponent.MessageBoxType.Confirm,
    //       true
    //     ),
    //     disableClose: false,
    //     autoFocus: false,
    //   })
    //   .afterClosed()
    //   .subscribe((result) => {
    //     if (result === 'confirm') {
    //       this.subjectService.getSubject(this.subjectId).subscribe((result: Subject) => {
    //         this.hyperLawService.deleteDocumentFromCasePreparationFile(result, file).subscribe(
    //           (result) => {
    //               this.eventService.filesUpdated();
    //               // do rename
    //           },
    //           (error) => {
    //             //cancel rename
    //           }
    //         );
    //       });
    //     }
    //   });
  }

  onContentReady(e) {
    this.contextMenuItems = e.component.option('contextMenu.items');
  }

  onContextMenuShowing(e: any) {
    let newItems = [...this.contextMenuItems];

    if (!e?.targetElement) {
      newItems = newItems.filter((i: any) => i.icon !== 'exportselected');
    }

    if (!this.selection || this.selection.length > 1) {
      newItems = newItems.filter((i: any) => i.icon !== 'tags');
    }

    e.component.option('contextMenu.items', newItems);
  }

  customizeColumns(columns) {
    const dateModifiedColumn = columns.find(
      (c: any) => c.dataField === 'dateModified'
    );
    dateModifiedColumn.format = 'dd/MMM/yyyy';
    dateModifiedColumn.calculateCellValue = (i: any) =>
      i.fileItem.isDirectory ? '' : i.fileItem.dateModified;

    const exportStatusIconColumn = columns.find(
      (c: any) => c.dataField === 'exportStatusIcon'
    );
    exportStatusIconColumn.cellTemplate = (cellElement: any, cellInfo: any) => {
      const fileItem: FileSystemItem = cellInfo?.data?.fileItem;

      if (!fileItem.isDirectory) {
        const exportStatus = fileItem.dataItem?.dataItem?.casePrepExportStatus;

        if (exportStatus) {
          const icon = document.createElement('img');
          icon.classList.add(
            'dx-icon',
            'dx-filemanager-details-item-thumbnail',
            'dx-filemanager-item-custom-thumbnail'
          );

          if (exportStatus == CasePrepExportStatus.Success) {
            icon.src = 'assets/images/svg/cloud_done.svg';
            icon.title = 'Export to Case Preparation File succeeded';
          } else if (exportStatus == CasePrepExportStatus.Failure) {
            icon.src = 'assets/images/svg/cloud_off.svg';
            icon.title = 'Export to Case Preparation File failed';
          } else {
            return;
          }

          cellElement.append(icon);
        }
      }
    };

    return columns;
  }

  onToolbarItemClick(e) {
    if ('options' in e.itemData) {
      e.itemData.options.onClick();

      if ((<string>e.itemData?.name).startsWith('Upload')) {
        e.component._commandManager.executeCommand('upload');
      }
    }
  }

  onContextMenuItemClick(e) {
    if ('options' in e.itemData) {
      e.itemData.options.onClick();
    }
  }

  onErrorOccurred(e) {
    let item = e.fileSystemItem;
    switch (e.errorCode) {
      case 1:
        e.errorText = `File already exists${
          item.parentPath === '' ? '' : ` in '${item.parentPath}'`
        }`;
        break;
      case 3:
        e.errorText = `Directory already exists${
          item.parentPath === '' ? '' : ` in '${item.parentPath}'`
        }`;
        break;
      case 7:
        e.errorText = item.isDirectory
          ? 'Directory names should only contain letters and numbers'
          : "File names should not contain the hash symbol ('#')";
        break;
      case undefined:
        if (item.size === 0 && !item.isDirectory) {
          e.errorText = 'Cannot upload empty file';
        }
    }
  }
}
